/**
 * Chat Agent Types and Constants
 * Defines the 5 AI Agents and their configurations
 */


export enum ChatAgentType {
  AI = 'AI', // AI chatbot (Hỏi đáp)
  TAX_QUESTION = 'TAX_QUESTION', // AI hỏi đáp thuế
  INSTRUCTION_MANUAL = 'INSTRUCTION_MANUAL', // AI hướng dẫn sử dụng
  ANALYTICS_QUESTION = 'ANALYTICS_QUESTION', // AI hỏi đáp dữ liệu shop
  CSKH = 'CSKH', // AI hỏi đáp CSKH
}

export interface ChatAgentConfig {
  type: ChatAgentType;
  name: string;
  displayName: string;
  placeholder: string;
  icon: string;
  shortName: string,
  welcomeMessage: string
}



export const AGENT_CONFIGS: Record<ChatAgentType, ChatAgentConfig> = {
  [ChatAgentType.AI]: {
    type: ChatAgentType.AI,
    name: 'AI',
    displayName: 'agents.AI.displayName',
    placeholder: 'agents.AI.placeholder',
    icon: 'CommonQAIcon',
    shortName: 'agents.AI.shortName',
    welcomeMessage: 'agents.AI.welcomeMessage'
  },
  [ChatAgentType.TAX_QUESTION]: {
    type: ChatAgentType.TAX_QUESTION,
    name: 'Thuế',
    displayName: 'agents.TAX_QUESTION.displayName',
    placeholder: 'agents.TAX_QUESTION.placeholder',
    icon: 'TaxIcon',
    shortName: 'agents.TAX_QUESTION.shortName',
    welcomeMessage: 'agents.TAX_QUESTION.welcomeMessage'
  },
  [ChatAgentType.INSTRUCTION_MANUAL]: {
    type: ChatAgentType.INSTRUCTION_MANUAL,
    name: 'HDSD',
    displayName: 'agents.INSTRUCTION_MANUAL.displayName',
    placeholder: 'agents.INSTRUCTION_MANUAL.placeholder',
    icon: 'UserManualIcon',
    shortName: 'agents.INSTRUCTION_MANUAL.shortName',
    welcomeMessage: 'agents.INSTRUCTION_MANUAL.welcomeMessage'
  },
  [ChatAgentType.ANALYTICS_QUESTION]: {
    type: ChatAgentType.ANALYTICS_QUESTION,
    name: 'Số liệu',
    displayName: 'agents.ANALYTICS_QUESTION.displayName',
    placeholder: 'agents.ANALYTICS_QUESTION.placeholder',
    icon: 'DatabaseIcon',
    shortName: 'agents.ANALYTICS_QUESTION.shortName',
    welcomeMessage: 'agents.ANALYTICS_QUESTION.welcomeMessage'
  },
  [ChatAgentType.CSKH]: {
    type: ChatAgentType.CSKH,
    name: 'CSKH',
    displayName: 'agents.CSKH.displayName',
    placeholder: 'agents.CSKH.placeholder',
    icon: 'CustomerServiceIcon',
    shortName: 'agents.CSKH.shortName',
    welcomeMessage: 'agents.CSKH.welcomeMessage'
  },
};

/**
 * Tag trong menu "+" của ô input gắn với agent (thay cho thanh tab agent cũ).
 * Chọn tag → chuyển sang agent tương ứng, gỡ tag → về Hỏi đáp (AI).
 */
export const AGENT_BY_FEATURE_TAG: Partial<Record<string, ChatAgentType>> = {
  tax: ChatAgentType.TAX_QUESTION,
  report: ChatAgentType.ANALYTICS_QUESTION,
  manual: ChatAgentType.INSTRUCTION_MANUAL,
};

export const FEATURE_TAG_BY_AGENT: Partial<Record<ChatAgentType, string>> = Object.fromEntries(
  Object.entries(AGENT_BY_FEATURE_TAG).map(([tag, agent]) => [agent, tag]),
);

export const isAgentFeatureTag = (key: string | null | undefined): boolean =>
  Boolean(key && AGENT_BY_FEATURE_TAG[key]);

/** Version payload socket chat - "5" bật field `tag` + `modelTier`. */
export const SOCKET_MESSAGE_VERSION = '5';

export type ModelTier = 'BASIC' | 'PRO' | 'MAX';

export const DEFAULT_MODEL_TIER: ModelTier = 'BASIC';
export type SocketMessageTag = 'IMAGE' | 'VIDEO' | 'AUDIO' | 'TAX' | 'REPORT' | 'GUIDE' | 'POST';

/** Tag thường (không gắn agent) chọn ở menu "+" → field `tag` gửi socket. */
export const SOCKET_TAG_BY_FEATURE_TAG: Partial<Record<string, SocketMessageTag>> = {
  image: 'IMAGE',
  video: 'VIDEO',
  audio: 'AUDIO',
  post: 'POST',
};

/** TAX / REPORT / GUIDE suy ra từ typeChat (agent) để khỏi phải giữ/clear thêm state tag riêng. */
export const SOCKET_TAG_BY_AGENT: Partial<Record<ChatAgentType, SocketMessageTag>> = {
  [ChatAgentType.TAX_QUESTION]: 'TAX',
  [ChatAgentType.ANALYTICS_QUESTION]: 'REPORT',
  [ChatAgentType.INSTRUCTION_MANUAL]: 'GUIDE',
};

/** Field bổ sung cho payload socket chat v5. */
export interface SocketMessageMeta {
  /** Chỉ tag thường; tag gắn agent tự suy từ typeChat trong sendChatMessage. */
  tag?: SocketMessageTag;
  modelTier?: ModelTier;
  action?: string;
  intent?: string;
  contextMap?: Record<string, unknown>;
  sendMessageUser?: boolean;
}
